/* eslint-disable max-len */
import { List, useLocaleState, useRefresh, useTranslate } from "react-admin";
import { getFilterValuesFromInterval } from "@react-admin/ra-calendar";
import * as User from "providers/user";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  DialogActions
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotesIcon from "@mui/icons-material/Notes";
import Popover from "@mui/material/Popover";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { IServiceAddOn } from "types";
import dayjs, { Dayjs } from "dayjs";
import CalendarArtistsFilter from "./CalendarArtistsFilter";
import AppointmentServiceShow from "./AppointmentServiceShow";
import AppointmentServiceEdit from "./AppointmentServiceEdit";
import CustomCalendar from "./CustomCalendar";
import { CustomButton } from "./CustomButton";
import { getZonedDate } from "../utils/helpers";
import { muiColors } from "themes/colors";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationModal from "./ConfirmationModal";
import * as Axios from "providers/axios";
import CancellationModal from "./CancellationModal";

export interface IShowAppointmentServiceDialogData {
  id?: string;
  client?: string;
  time?: string;
  artistId?: string;
  artist?: string;
  serviceName?: string;
  date?: string;
  artistPhoto?: string;
  clientPhoto?: string;
  guestName?: string;
  appointmentId?: string;
  appointmentNotes?: string;
  serviceDuration?: number;
  servicePrice?: number;
  locationName?: string;
  addOns?: IServiceAddOn[];
  startDateAndTime?: Dayjs;
  locationTimezone?: string;
  isBlockedTime?: boolean;
  clientPhoneNumber?: string;
}

export default function AppointmentServiceList({
  selectedView,
  setSelectedView
}: {
  selectedView: string;
  setSelectedView: Dispatch<SetStateAction<string>>;
}) {
  const { user, permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const location = useLocation();
  const translate = useTranslate();
  const refresh = useRefresh();
  const [localeState] = useLocaleState();
  const [showDialogData, setShowDialogData] =
    useState<IShowAppointmentServiceDialogData | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeNote, setActiveNote] = useState<string>("");
  const [editDialogId, setEditDialogId] = useState<string>("");
  const [forceLoading, setForceLoading] = useState<boolean>(true);
  const [artistFilter, setArtistFilter] = useState<string>("");
  const [blockTimeDeleteId, setBlockTimeDeleteId] = useState<
    string | undefined
  >("");
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const dateFilterFromLocalStorage = localStorage.getItem(
    "ServiceCalendarDateFilter"
  );
  const parsedDateFilter =
    dateFilterFromLocalStorage !== null && dateFilterFromLocalStorage !== "null"
      ? dayjs(JSON.parse(dateFilterFromLocalStorage))
      : null;
  const [calendarsDate, setCalendarsDate] = useState<Dayjs | null>(
    parsedDateFilter
  );
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<{
    title: string;
    dateForFilter: string;
  } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handlePopoverOpen = (note: string) => {
    if (popoverRef.current) {
      setActiveNote(note);
      setAnchorEl(popoverRef.current);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveNote("");
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    localStorage.removeItem("RaStore.Appointment.listParams");
    localStorage.removeItem("RaStore.AppointmentService.listParams");
    const timer = setTimeout(() => {
      // this is quite a "hacking" solution but we need it, because
      // if you reload page and calendar is rendered immediately,
      // it causes drag end drop problem
      // (the event is duplicated on drag and is updated after 5-10 sec)
      setForceLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (window.location.search) {
      // react admin or calendar is adding some long queries in url which is
      // doing nothing (just an ugly long url),
      // because of that I'm clearing url. Yes, the browser's back
      // button will not do anything until you will click
      // enough times to go back, but it is not because of this, without
      // clearing url the back button would do the same
      // but would be changing random long queries until you really go back.
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [location]);

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  const filter = {
    appointment: {
      is: {
        location_id: { in: locationIds },
        cancelled_at: { equals: null },
        start_time: {
          gte: selectedCalendarDate?.dateForFilter
            ? dayjs(selectedCalendarDate.dateForFilter)
                .startOf(selectedView === "timeGridWeek" ? "week" : "month")
                .format()
            : dayjs()
                .startOf(selectedView === "timeGridWeek" ? "week" : "month")
                .format(),
          lte: selectedCalendarDate?.dateForFilter
            ? dayjs(selectedCalendarDate.dateForFilter)
                .endOf(selectedView === "timeGridWeek" ? "week" : "month")
                .format()
            : dayjs()
                .endOf(selectedView === "timeGridWeek" ? "week" : "month")
                .format()
        },
        ...(permissions.isArtist && {
          appointment_services: {
            some: {
              artist: {
                is: {
                  user_id: user.id
                }
              }
            }
          }
        })
      }
    }
  };

  const closeShowDialog = () => {
    setShowDialogData(null);
  };
  const closeEditDialog = () => {
    setEditDialogId("");
    refresh();
  };

  const deleteBlockTimeHandler = async (appointmentId: string) => {
    try {
      await api.delete(`/v1/artists/block-time/${appointmentId}`);
      setBlockTimeDeleteId("");
      setShowDialogData(null);
      refresh();
    } catch (error) {
      setBlockTimeDeleteId("");
    }
  };

  const converter = (event: any) => {
    const price = !!event?.add_ons?.length
      ? event?.service?.price +
        event.add_ons.reduce((acc: number, curr: any) => {
          acc += curr?.core_service?.price;
          return acc;
        }, 0)
      : event?.service?.price;
    const start = getZonedDate(
      event?.start_time,
      event?.appointment?.location?.timezone
    );
    const end = getZonedDate(
      event?.end_time,
      event?.appointment?.location?.timezone
    );
    const guestName =
      event?.person_number > 0
        ? `${event?.user?.first_name}'s friend (P${event?.person_number + 1})`
        : "";
    const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const retval = {
      id: String(event.id),
      title: `${event?.user?.first_name} ${event?.user?.last_name}`,
      start: start.toISOString(),
      end: end.toISOString(),
      extendedProps: {
        artistsIds: event?.artist?.user?.id,
        artistId: event?.artist?.id,
        artistFullName: `${event?.artist?.user?.first_name} ${event?.artist?.user?.last_name}`,
        artistPhoto:
          event?.artist?.cover_photo_url ||
          event?.artist?.user?.photo_url ||
          "monogram",
        clientPhoto: event?.user?.photo_url || "monogram",
        guestName,
        appointmentId: event?.appointment?.id,
        appointmentNotes: event?.appointment?.notes,
        duration: durationInMinutes,
        serviceDuration: event?.service?.duration,
        servicePrice: price,
        serviceName: event?.service?.name,
        locationName:
          event?.appointment?.location?.nickname ||
          event?.appointment?.location?.name,
        addOns: event?.add_ons || [],
        time: `${start.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })} - ${end.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })}`,
        startDateAndTime: event?.start_time,
        locationTimezone: event?.appointment?.location?.timezone,
        isBlockedTime: event?.is_blocked_time,
        clientPhoneNumber: event?.user?.phone_number
      },
      backgroundColor: event?.is_blocked_time
        ? "#dbdbdb"
        : durationInMinutes > 0 && durationInMinutes < 31
        ? "#FFDAB4"
        : durationInMinutes > 30 && durationInMinutes < 61
        ? "#D9ECF1"
        : "#F8D1C0",
      borderColor: event?.is_blocked_time
        ? "#dbdbdb"
        : durationInMinutes > 0 && durationInMinutes < 30
        ? "#FFDAB4"
        : durationInMinutes > 30 && durationInMinutes < 61
        ? "#D9ECF1"
        : "#F8D1C0",
      textColor: event?.is_blocked_time
        ? "#000000"
        : durationInMinutes > 0 && durationInMinutes < 30
        ? "#66411B"
        : durationInMinutes > 30 && durationInMinutes < 61
        ? "#114B5B"
        : "#752D0D",
      editable: false,
      durationEditable: false,
      display: "block",
      duration: durationInMinutes,
      start_time: start,
      end_time: end
    };
    return retval;
  };

  if (forceLoading) return <div />;

  return (
    <List
      resource="AppointmentService"
      filterDefaultValues={getFilterValuesFromInterval()}
      perPage={1000}
      pagination={false}
      filter={filter}
      actions={<div style={{ height: "0px" }} />}
      sx={{
        "& .MuiBox-root": {
          padding: 0
        },
        "& .MuiToolbar-root": {
          minHeight: "0px",
          height: "0px"
        },
        "& .RaList-content": {
          backgroundColor: "#F6F4F1",
          padding: "0px 16px 0px 0px",
          boxShadow: "none"
        }
      }}
    >
      <div
        ref={popoverRef}
        onClick={() => !!activeNote && handlePopoverClose()}
        aria-hidden
      >
        <CalendarArtistsFilter
          artistFilter={artistFilter}
          setArtistFilter={setArtistFilter}
          permissions={permissions}
        />
        <CustomCalendar
          locale={localeState}
          convertToEvent={converter}
          editable={false}
          selectable={false}
          artistFilter={artistFilter}
          anchorEl={anchorEl}
          setShowDialogData={setShowDialogData}
          isPopoverOpen={open}
          handlePopoverOpen={handlePopoverOpen}
          handlePopoverClose={handlePopoverClose}
          type={"appointmentService"}
          pageTitle={translate("navigation.services_calendar")}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          calendarsDate={calendarsDate}
          setCalendarsDate={setCalendarsDate}
          setSelectedCalendarDate={setSelectedCalendarDate}
        />
      </div>
      {!!showDialogData && (
        <Dialog
          open={!!showDialogData}
          onClose={closeShowDialog}
          sx={{
            "& .MuiDialogContent-root": {
              padding: "0px"
            },
            "& .MuiDialog-paper": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              maxWidth: "520px",
              width: "100%"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
            <Button
              sx={{ minWidth: "unset", whiteSpace: "nowrap" }}
              startIcon={<NotesIcon />}
              onClick={() => {
                if (showDialogData?.appointmentId) {
                  setEditDialogId(showDialogData.appointmentId);
                }
              }}
            >
              {translate("appointment.note")}
            </Button>
            <div className="w-full flex uppercase justify-center items-center  text-[14px] font-['Inter'] font-[600]">
              {showDialogData?.isBlockedTime
                ? `${translate("appointment.blocked_time")}`
                : "POINTMENT"}
            </div>
            <IconButton onClick={closeShowDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <AppointmentServiceShow
              showDialogData={showDialogData}
              setShowDialogData={setShowDialogData}
              refresh={() => refresh()}
            />
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: showDialogData?.isBlockedTime
                ? "flex-end"
                : "space-between"
            }}
          >
            {showDialogData?.isBlockedTime ? (
              <CustomButton
                text={translate("common.delete")}
                variant="secondary"
                onClick={() =>
                  setBlockTimeDeleteId(showDialogData?.appointmentId)
                }
                sx={{
                  color: muiColors.error,
                  border: `solid 1px ${muiColors.error}`,
                  svg: {
                    fill: muiColors.error,
                    path: {
                      fill: muiColors.error
                    }
                  }
                }}
                startIcon={<DeleteIcon />}
              />
            ) : permissions?.isArtist ? (
              <div />
            ) : (
              <CustomButton
                text={translate("appointment.cancel_full_appointment")}
                variant="secondary"
                onClick={() =>
                  setCancelModalAppointmentId(
                    showDialogData?.appointmentId || null
                  )
                }
                sx={{
                  color: muiColors.error,
                  border: `solid 1px ${muiColors.error}`,
                  svg: {
                    fill: muiColors.error,
                    path: {
                      fill: muiColors.error
                    }
                  }
                }}
              />
            )}
            {!showDialogData?.isBlockedTime && (
              <Link
                to={`/dashboard/Appointment/${showDialogData?.appointmentId}/show`}
              >
                <CustomButton
                  variant="secondary"
                  text={translate("appointment.appointment_details")}
                />
              </Link>
            )}
          </DialogActions>
          {blockTimeDeleteId && (
            <ConfirmationModal
              open={!!blockTimeDeleteId}
              handleConfirm={() => deleteBlockTimeHandler(blockTimeDeleteId)}
              onClose={() => setBlockTimeDeleteId("")}
              text="Are you sure you want to delete? This action can't be reversed, so please be certain before proceeding."
              buttonText="Delete"
            />
          )}
          {!blockTimeDeleteId && cancelModalAppointmentId && (
            <CancellationModal
              cancelModalAppointmentId={cancelModalAppointmentId}
              setCancelModalAppointmentId={setCancelModalAppointmentId}
              refreshCalendarAndCloseShowModal={() => {
                refresh();
                closeShowDialog();
              }}
            />
          )}
        </Dialog>
      )}
      {!!editDialogId && (
        <Dialog
          open={!!editDialogId}
          onClose={() => setEditDialogId("")}
          sx={{
            "& .MuiDialogContent-root": {
              padding: "0px"
            },
            "& .MuiDialog-paper": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              maxWidth: "410px",
              width: "100%"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600]">
              Note
            </div>
            <IconButton onClick={() => setEditDialogId("")}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <AppointmentServiceEdit
              editDialogId={editDialogId}
              closeEditDialog={closeEditDialog}
              setShowDialogData={setShowDialogData}
            />
          </DialogContent>
        </Dialog>
      )}
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: "none"
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className="bg-mono-white p-[70px] max-w-[500px] text-[22px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor">
          {activeNote}
        </div>
      </Popover>
    </List>
  );
}
